/**
 * Request Manager - Gestionează prioritizarea cererilor HTTP
 * 
 * Cererile către /user pentru verificarea token-ului au prioritate maximă
 * și vor fi executate înaintea celorlalte cereri în așteptare.
 */

class RequestManager {
  constructor() {
    this.pendingRequests = [];
    this.activeRequests = new Set();
    this.maxConcurrentRequests = 6; // Limita browserului pentru HTTP/1.1
    this.isProcessing = false;
    this.tokenCheckInProgress = false;
    this.tokenCheckPromise = null; // Promise pentru verificarea în curs
  }

  /**
   * Adaugă o cerere în coadă cu prioritate
   * @param {Function} requestFn - Funcția care returnează un Promise (fetch)
   * @param {string} priority - 'high', 'normal', 'low'
   * @returns {Promise}
   */
  async enqueue(requestFn, priority = 'normal') {
    return new Promise((resolve, reject) => {
      const request = {
        fn: requestFn,
        priority: this.getPriorityValue(priority),
        resolve,
        reject,
        timestamp: Date.now()
      };

      // Inserează cererea în funcție de prioritate
      const insertIndex = this.pendingRequests.findIndex(
        req => req.priority < request.priority
      );

      if (insertIndex === -1) {
        this.pendingRequests.push(request);
      } else {
        this.pendingRequests.splice(insertIndex, 0, request);
      }

      this.processQueue();
    });
  }

  /**
   * Marchează că o verificare de token este în curs
   */
  setTokenCheckInProgress(inProgress, promise = null) {
    this.tokenCheckInProgress = inProgress;
    this.tokenCheckPromise = promise;
    if (!inProgress) {
      this.tokenCheckPromise = null;
      // Când verificarea token-ului se termină, procesează coada
      this.processQueue();
    }
  }

  /**
   * Verifică dacă o verificare de token este în curs
   */
  isTokenCheckInProgress() {
    return this.tokenCheckInProgress;
  }

  /**
   * Așteaptă ca verificarea token-ului să se termine
   */
  async waitForTokenCheck() {
    if (this.tokenCheckPromise) {
      try {
        await this.tokenCheckPromise;
      } catch (err) {
        // Ignoră erorile - ne interesează doar să așteptăm
      }
    }
  }

  /**
   * Procesează coada de cereri
   */
  async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    while (
      this.pendingRequests.length > 0 &&
      this.activeRequests.size < this.maxConcurrentRequests
    ) {
      // Dacă o verificare de token este în curs, amână alte cereri
      if (this.tokenCheckInProgress && this.pendingRequests[0].priority < 100) {
        break;
      }

      const request = this.pendingRequests.shift();
      this.activeRequests.add(request);

      request
        .fn()
        .then(result => {
          this.activeRequests.delete(request);
          request.resolve(result);
          this.processQueue();
        })
        .catch(error => {
          this.activeRequests.delete(request);
          request.reject(error);
          this.processQueue();
        });
    }

    this.isProcessing = false;
  }

  /**
   * Convertește prioritatea text în valoare numerică
   */
  getPriorityValue(priority) {
    const priorities = {
      critical: 100, // Pentru verificarea token-ului
      high: 75,
      normal: 50,
      low: 25
    };
    return priorities[priority] || priorities.normal;
  }

  /**
   * Curăță cererile vechi din coadă (opțional)
   */
  clearStaleRequests(maxAge = 30000) {
    const now = Date.now();
    this.pendingRequests = this.pendingRequests.filter(
      req => now - req.timestamp < maxAge
    );
  }
}

// Singleton instance
const requestManager = new RequestManager();

// Salvează fetch-ul original
const originalFetch = window.fetch;

/**
 * Wrapper pentru fetch cu prioritizare
 * @param {string|URL} url - URL-ul cererii
 * @param {object} options - Opțiuni fetch
 * @param {string} priority - Prioritatea cererii: 'critical', 'high', 'normal', 'low'
 * @returns {Promise}
 */
export const priorityFetch = (url, options = {}, priority = 'normal') => {
  // Convertește URL object la string dacă e necesar
  const urlString = typeof url === 'string' ? url : url.toString();
  
  // Dacă este o cerere către /user, folosește prioritate critică
  if (urlString.includes('/user') && !urlString.includes('/users')) {
    priority = 'critical';
  }

  return requestManager.enqueue(() => {
    // Adaugă priority în opțiunile fetch dacă browserul suportă
    const fetchOptions = {
      ...options,
      priority: priority === 'critical' || priority === 'high' ? 'high' : 'auto'
    };

    return originalFetch(urlString, fetchOptions);
  }, priority);
};

/**
 * Patch global pentru fetch - interceptează TOATE cererile
 */
export const patchGlobalFetch = () => {
  window.fetch = function(url, options = {}) {
    // Convertește URL object la string dacă e necesar
    const urlString = typeof url === 'string' ? url : url.toString();
    
    // Determină prioritatea bazată pe URL
    let priority = 'normal';
    
    // Prioritate critică pentru verificarea token-ului
    if (urlString.includes('/user') && !urlString.includes('/users')) {
      priority = 'critical';
    }
    // Prioritate scăzută pentru statistici, rapoarte, etc.
    else if (urlString.includes('/statistici') || urlString.includes('/rapoarte')) {
      priority = 'low';
    }

    // Folosește requestManager pentru toate cererile
    return requestManager.enqueue(() => {
      const fetchOptions = {
        ...options,
        priority: priority === 'critical' || priority === 'high' ? 'high' : 'auto'
      };
      return originalFetch(urlString, fetchOptions);
    }, priority);
  };
};

/**
 * Restaurează fetch-ul original (pentru debugging)
 */
export const unpatchGlobalFetch = () => {
  window.fetch = originalFetch;
};

/**
 * Marchează începutul/sfârșitul verificării token-ului
 */
export const setTokenCheckInProgress = (inProgress, promise = null) => {
  requestManager.setTokenCheckInProgress(inProgress, promise);
};

/**
 * Verifică dacă o verificare de token este în curs
 */
export const isTokenCheckInProgress = () => {
  requestManager.isTokenCheckInProgress();
};

/**
 * Așteaptă ca verificarea token-ului să se termine
 * Folosește asta înainte de a face cereri importante
 */
export const waitForTokenCheck = async () => {
  await requestManager.waitForTokenCheck();
};

export default requestManager;
